// src/pages/IntegrationsPage.tsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQuery } from '../hooks/useAuthenticatedQuery';
import { baseClient } from '../api/baseClient';
import {
  Box, Typography, CircularProgress, Alert, Paper, Grid,
  Container, Card, CardContent, Button, Switch, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction,
  Divider, Avatar
} from '@mui/material';
import {
  Email as EmailIcon, CalendarToday as CalendarIcon,
  Storage as DatabaseIcon, Cloud as CloudIcon,
  Security as SecurityIcon, Api as ApiIcon,
  CheckCircle as CheckIcon, Error as ErrorIcon,
  Settings as SettingsIcon, Add as AddIcon,
  Sync as SyncIcon, Warning as WarningIcon,
  ImportExport as ImportExportIcon
} from '@mui/icons-material';
import ImportExportPanel from '../components/ImportExportPanel';

interface Integration {
  id: string;
  name: string;
  type: 'email' | 'calendar' | 'storage' | 'api' | 'crm' | 'marketing';
  status: 'connected' | 'disconnected' | 'error' | 'syncing';
  description: string;
  lastSync?: string;
  config?: Record<string, any>;
}

interface IntegrationFormData {
  name: string;
  type: string;
  apiKey: string;
  endpoint: string;
  description: string;
}

// Mock data for integrations
const mockIntegrations: Integration[] = [
  {
    id: '1',
    name: 'Google Workspace',
    type: 'email',
    status: 'connected',
    description: 'Email and calendar synchronization',
    lastSync: '2024-01-15T10:30:00Z'
  },
  {
    id: '2',
    name: 'Microsoft 365',
    type: 'email',
    status: 'disconnected',
    description: 'Office 365 email integration'
  },
  {
    id: '3',
    name: 'Salesforce',
    type: 'crm',
    status: 'error',
    description: 'Legacy CRM data migration',
    lastSync: '2024-01-14T15:45:00Z'
  },
  {
    id: '4',
    name: 'HubSpot',
    type: 'marketing',
    status: 'connected',
    description: 'Marketing automation platform',
    lastSync: '2024-01-15T09:15:00Z'
  }
];

// Helper function to get integration icon
const getIntegrationIcon = (type: string) => {
  switch (type) {
    case 'email': return <EmailIcon />;
    case 'calendar': return <CalendarIcon />;
    case 'storage': return <DatabaseIcon />;
    case 'api': return <ApiIcon />;
    case 'crm': return <CloudIcon />;
    case 'marketing': return <CloudIcon />;
    default: return <ApiIcon />;
  }
};

// Helper function to get status color
const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' => {
  switch (status) {
    case 'connected': return 'success';
    case 'error': return 'error';
    case 'syncing': return 'warning';
    default: return 'info';
  }
};

// Helper function to get status icon
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'connected': return <CheckIcon color="success" />;
    case 'error': return <ErrorIcon color="error" />;
    case 'syncing': return <SyncIcon color="warning" />;
    default: return <WarningIcon color="info" />;
  }
};

export function IntegrationsPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [formData, setFormData] = useState<IntegrationFormData>({
    name: '',
    type: 'api',
    apiKey: '',
    endpoint: '',
    description: ''
  });
  const [formError, setFormError] = useState('');

  const queryClient = useQueryClient();

  // Fetch real integration data from the API (only when authenticated)
  const { data: integrations = [], isLoading, isError } = useAuthenticatedQuery<Integration[]>({
    queryKey: ['integrations'],
    queryFn: async () => {
      try {
        // For now, return a combination of real user OAuth tokens and available integrations
        let userProfile = null;
        try {
          userProfile = await baseClient.get('/user-profiles/profiles/me');
        } catch (error) {
          // If user profile fails (e.g., not authenticated), use fallback data
          console.log('User profile not available, using fallback data');
        }
        const availableIntegrations: Integration[] = [
          {
            id: 'google',
            name: 'Google Workspace',
            type: 'email',
            status: userProfile?.google_access_token ? 'connected' : 'disconnected',
            description: 'Email and calendar synchronization',
            lastSync: userProfile?.google_access_token ? new Date().toISOString() : undefined
          },
          {
            id: 'microsoft',
            name: 'Microsoft 365',
            type: 'email',
            status: userProfile?.microsoft_access_token ? 'connected' : 'disconnected',
            description: 'Office 365 email integration',
            lastSync: userProfile?.microsoft_access_token ? new Date().toISOString() : undefined
          },
          {
            id: 'salesforce',
            name: 'Salesforce',
            type: 'crm',
            status: 'available',
            description: 'Legacy CRM data migration'
          },
          {
            id: 'hubspot',
            name: 'HubSpot',
            type: 'marketing',
            status: 'available',
            description: 'Marketing automation platform'
          }
        ];
        return availableIntegrations;
      } catch (error) {
        console.error('Error fetching integrations:', error);
        // Fallback to available integrations without connection status
        return [
          {
            id: 'google',
            name: 'Google Workspace',
            type: 'email',
            status: 'available',
            description: 'Email and calendar synchronization'
          },
          {
            id: 'microsoft',
            name: 'Microsoft 365',
            type: 'email',
            status: 'available',
            description: 'Office 365 email integration'
          },
          {
            id: 'salesforce',
            name: 'Salesforce',
            type: 'crm',
            status: 'available',
            description: 'Legacy CRM data migration'
          },
          {
            id: 'hubspot',
            name: 'HubSpot',
            type: 'marketing',
            status: 'available',
            description: 'Marketing automation platform'
          }
        ];
      }
    }
  });

  // Mock mutation for creating integrations
  const createIntegrationMutation = useMutation({
    mutationFn: async (integrationData: IntegrationFormData) => {
      // In production: return await integrationsApi.createIntegration(integrationData);
      return Promise.resolve({ id: Date.now().toString(), ...integrationData, status: 'disconnected' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setDialogOpen(false);
      setFormData({ name: '', type: 'api', apiKey: '', endpoint: '', description: '' });
      setFormError('');
    },
    onError: (error: any) => {
      setFormError('Failed to create integration');
    }
  });

  // Mock mutation for toggling integration status
  const toggleIntegrationMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      // In production: return await integrationsApi.toggleIntegration(id, enabled);
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    }
  });

  const handleCreateIntegration = () => {
    if (!formData.name || !formData.type) {
      setFormError('Name and Type are required');
      return;
    }
    createIntegrationMutation.mutate(formData);
  };

  const handleToggleIntegration = (integration: Integration) => {
    if (integration.id === 'google') {
      handleGoogleOAuth();
    } else if (integration.id === 'microsoft') {
      handleMicrosoftOAuth();
    } else {
      const enabled = integration.status !== 'connected';
      toggleIntegrationMutation.mutate({ id: integration.id, enabled });
    }
  };

  const handleGoogleOAuth = () => {
    // Redirect to Google OAuth
    window.location.href = `${import.meta.env.VITE_API_BASE_URL || ''}/api/v1/integrations/login/google`;
  };

  const handleMicrosoftOAuth = () => {
    // Redirect to Microsoft OAuth
    window.location.href = `${import.meta.env.VITE_API_BASE_URL || ''}/api/v1/integrations/login/microsoft`;
  };

  const handleConfigureIntegration = (integration: Integration) => {
    setSelectedIntegration(integration);
    if (integration.id === 'google' || integration.id === 'microsoft') {
      // For OAuth integrations, show connection status and sync options
      alert(`${integration.name} OAuth integration. Use the toggle to connect/disconnect.`);
    } else {
      // Open configuration dialog for other integrations
      setDialogOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load integrations. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Integrations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage third-party integrations and API connections
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Add Integration
        </Button>
      </Box>

      {/* Integration Categories */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmailIcon color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h6">
                    {integrations.filter(i => i.type === 'email').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Email & Calendar</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CloudIcon color="success" fontSize="large" />
                <Box>
                  <Typography variant="h6">
                    {integrations.filter(i => i.type === 'crm').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">CRM Systems</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ApiIcon color="info" fontSize="large" />
                <Box>
                  <Typography variant="h6">
                    {integrations.filter(i => i.type === 'api').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">API Connections</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SecurityIcon color="warning" fontSize="large" />
                <Box>
                  <Typography variant="h6">
                    {integrations.filter(i => i.status === 'connected').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Active Connections</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Integrations List */}
      <Paper>
        <List>
          {integrations.map((integration, index) => (
            <React.Fragment key={integration.id}>
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {getIntegrationIcon(integration.type)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} component="div">
                      <Typography variant="body1" fontWeight="medium" component="div">
                        {integration.name}
                      </Typography>
                      <Chip
                        label={integration.status}
                        size="small"
                        color={getStatusColor(integration.status)}
                        icon={getStatusIcon(integration.status)}
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary" component="span">
                        {integration.description}
                      </Typography>
                      {integration.lastSync && (
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block', mt: 0.5 }}>
                          Last sync: {new Date(integration.lastSync).toLocaleString()}
                        </Typography>
                      )}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(integration.id === 'google' || integration.id === 'microsoft') ? (
                      <Button
                        size="small"
                        variant={integration.status === 'connected' ? 'outlined' : 'contained'}
                        color={integration.status === 'connected' ? 'error' : 'primary'}
                        onClick={() => handleToggleIntegration(integration)}
                        disabled={integration.status === 'syncing'}
                      >
                        {integration.status === 'connected' ? 'Disconnect' : 'Connect OAuth'}
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="small"
                          startIcon={<SettingsIcon />}
                          onClick={() => handleConfigureIntegration(integration)}
                        >
                          Configure
                        </Button>
                        <Switch
                          checked={integration.status === 'connected'}
                          onChange={() => handleToggleIntegration(integration)}
                          disabled={integration.status === 'syncing'}
                        />
                      </>
                    )}
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
              {index < integrations.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Data Import/Export Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ImportExportIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Data Import & Export
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage your CRM data with powerful import and export tools. Perfect for data migration,
          backups, and sharing with your team.
        </Typography>

        <ImportExportPanel />
      </Paper>

      {/* Create Integration Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Integration</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Integration Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!formData.name && formError.includes('Name')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Integration Type *</InputLabel>
                <Select
                  value={formData.type}
                  label="Integration Type *"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <MenuItem value="email">Email & Calendar</MenuItem>
                  <MenuItem value="crm">CRM System</MenuItem>
                  <MenuItem value="marketing">Marketing Platform</MenuItem>
                  <MenuItem value="storage">Cloud Storage</MenuItem>
                  <MenuItem value="api">Custom API</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key"
                type="password"
                value={formData.apiKey}
                onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                helperText="Enter your API key or access token"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Endpoint URL"
                value={formData.endpoint}
                onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
                helperText="API endpoint URL (if applicable)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                helperText="Brief description of this integration"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateIntegration}
            variant="contained"
            disabled={createIntegrationMutation.isLoading}
          >
            {createIntegrationMutation.isLoading ? 'Creating...' : 'Create Integration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
